import React from 'react';

import Helmet from 'react-helmet';
import { useTitle } from 'ha/helpers/hooks/useTitle';
import { TranslateFunc, Language } from '@ha/intl';
import { useIntl } from 'ha/i18n';
import { useLocation } from 'react-router-dom';
import {
  ListingKind,
  Listing,
  ListingState,
  ListingPhoto,
} from '@ha/api/v2/types/Listing';
import { UnitType } from '@ha/api/v2/types/inventory/UnitType';
import { isListingFurnished } from 'ha/models/ListingFacilities/isListingFurnished';
import { ListingTypeTranslations } from 'ha/models/Listing/translations';
import { FacilitiesValues } from 'ha/models/ListingFacilities/constants';
import { FacilitiesTranslations } from 'ha/models/ListingFacilities/translations';
import { formatPrice } from 'ha/utils/formatPrice';
import { formatHrefURL as formatHrefUrl } from 'ha/utils/formatHrefURL';
import { ImgixSize } from 'ha/modules/Image/constants';
import { useImgix } from 'ha/modules/Image/useImgix';

import { getPageTitle } from '../helpers/getPageTitle';
import { getTenantStatusTitle } from '../helpers/getTenantStatusTitle';
import { getMetaTitle } from '../helpers/getMetaTitle';
import { getOGDescription } from '../helpers/getOGDescription';
import { getMetaDescription } from '../helpers/getMetaDescription';

function formatBathroomLabel(val: string, T: TranslateFunc) {
  switch (val) {
    case FacilitiesValues.SharedWithBoth:
    case FacilitiesValues.SharedWithGirls:
    case FacilitiesValues.SharedWithBoys:
      return T('shared bathroom');
    case FacilitiesValues.Private:
      return T('private bathroom');
    default:
      return null;
  }
}

function formatKitchenLabel(val: string, T: TranslateFunc) {
  switch (val) {
    case FacilitiesValues.Shared:
      return T('shared kitchen');
    case FacilitiesValues.Private:
      return T('private kitchen');
    default:
      return null;
  }
}
export interface Props {
  listing: Listing & Partial<UnitType>;
  mainPhoto?: string;
  images?: ListingPhoto[];
  isImperialSystem: boolean;
}

const ListingHelmetInternal: React.FC<Props> = ({
  listing: {
    id,
    city,
    cityLocalized,
    currency,
    facilities,
    price,
    kind,
    type,
    street,
    listingPath,
    description: listingDescription,
    state,
    belongsToPartner,
    latitude,
    longitude,
    costs,
    freePlaces,
    unitTypePath,
  },
  mainPhoto = '',
  images,
  isImperialSystem,
}) => {
  const location = useLocation();
  const { T, urlResolver } = useIntl();
  const { transformImageURL } = useImgix();

  const place = `${city || cityLocalized}, ${street}`;
  const formattedPrice = formatPrice(price, currency);

  const hasCoordinates =
    typeof latitude === 'number' && typeof longitude === 'number';

  const isFurnished = isListingFurnished({ facilities });

  let listingType;

  if (kind === ListingKind.EntirePlace && facilities.bedroom_count === '0') {
    listingType = T('Studio');
  } else if (kind === ListingKind.EntirePlace) {
    listingType = T(ListingTypeTranslations[type]);
  } else {
    listingType = T('Room');
  }

  const description = T(
    '%(type)s for rent for %(price)s per month in %(place)s',
    {
      place,
      price: formattedPrice,
      type: listingType,
    },
  );

  const facilitiesDescription = `${[
    isFurnished ? T('Furnished') : '',
    Number(facilities.bedroom_count)
      ? T('%s bedroom(s)', facilities.bedroom_count)
      : null,
    formatBathroomLabel(facilities.bathroom!, T),
    formatKitchenLabel(facilities.kitchen!, T),
    facilities.wifi === FacilitiesValues.Yes
      ? T(FacilitiesTranslations.wifi)
      : null,
    facilities.tv === FacilitiesValues.Yes
      ? T(FacilitiesTranslations.tv)
      : null,
  ]
    .filter(i => !!i)
    .join(', ')}${getTenantStatusTitle(T, facilities.tenant_status!)}`;

  const structuredData = {
    '@context': 'http://schema.org',
    '@type': 'Accommodation',
    name: `${T('%(type)s for rent in %(place)s', {
      place,
      type: listingType,
    })}`,
    description: `${description}${
      facilitiesDescription ? `: ${facilitiesDescription}` : ''
    }.`,
    numberOfRooms: facilities.bedroom_count || 1,
    photo: mainPhoto || null,
    geo: hasCoordinates
      ? {
          '@type': 'GeoCoordinates',
          latitude: latitude.toString(),
          longitude: longitude.toString(),
        }
      : null,
    potentialAction: {
      '@type': 'ReserveAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${urlResolver.getBaseUrl()}${location.pathname}`,
        actionPlatform: [
          'http://schema.org/DesktopWebPlatform',
          'http://schema.org/MobileWebPlatform',
        ],
      },
      result: {
        '@type': 'LodgingReservation',
        name: T('Rent Now'),
      },
    },
  };

  const pageTitle = getPageTitle(T, listingType, place, id);
  const metaDescription = getMetaDescription(
    T,
    listingType,
    formattedPrice,
    place,
    facilitiesDescription,
  );
  const htmlTitle = useTitle(pageTitle);

  const isIndexed = !belongsToPartner && state === ListingState.PUBLISHED;

  const ogTitle = getMetaTitle({
    T,
    type,
    kind,
    isFurnished,
    facilities,
    price: formattedPrice,
    city,
  });

  const ogDescription = getOGDescription({
    T,
    kind,
    costs: costs.costs,
    freePlaces,
    facilities,
    listingDescription,
    isImperialSystem,
  });

  const ogImage =
    images && images.length > 0
      ? transformImageURL(images[0].url, {
          ...ImgixSize.fullscreenDesktopSmall,
          fit: 'crop',
        })
      : '';

  return (
    <Helmet>
      <script type="application/ld+json" key="structuredDataListing">
        {JSON.stringify(structuredData)}
      </script>

      {!isIndexed ? (
        <meta key="meta robots" name="robots" content="noindex, nofollow" />
      ) : (
        <link
          key="link canonical"
          rel="canonical"
          href={urlResolver.absolutizePath(
            listingPath || (unitTypePath as string),
          )}
        />
      )}

      <link
        key="link alternate x-default"
        rel="alternate"
        hrefLang="x-default"
        href={`${urlResolver.getBaseUrl()}${formatHrefUrl(
          listingPath || unitTypePath,
          Language.en,
        )}`}
      />

      {Object.values(Language).map((language: Language) => (
        <link
          key={`link alternate ${language}`}
          rel="alternate"
          hrefLang={language}
          href={`${urlResolver.getBaseUrl()}${formatHrefUrl(
            listingPath || unitTypePath,
            language,
          )}`}
        />
      ))}

      <meta key="meta og:title" property="og:title" content={ogTitle} />
      <meta
        key="meta og:description"
        property="og:description"
        content={ogDescription}
      />
      {ogImage && <meta property="og:image" content={ogImage} />}

      <meta
        key="meta twitter:card"
        property="twitter:card"
        content="summary_large_image"
      />
      <meta
        key="meta twitter:title"
        property="twitter:title"
        content={ogTitle}
      />
      <meta
        key="meta twitter:description"
        property="twitter:description"
        content={listingDescription}
      />
      {ogImage && (
        <meta
          key="meta twitter:image"
          property="twitter:image"
          content={ogImage}
        />
      )}

      <title key="title">{htmlTitle}</title>
      <meta
        key="meta description"
        name="description"
        content={metaDescription}
      />
    </Helmet>
  );
};

const ListingHelmet = React.memo(ListingHelmetInternal, () => true);
export { ListingHelmet };
